import { useState } from "react";
import Select from "../../components/Select.jsx";
import { EYEBROW_CLASS, NOTE_CLASS } from "../../lib/ui.js";
import { valueAt } from "./catalogDocument.js";
import ChangeList from "./ChangeList.jsx";
import { plural } from "./StepStrip.jsx";
import { PRIMARY_ACTION } from "./styles.js";
import { Blocker, Interaction, WorkflowImpact } from "./textRoles.jsx";
import { REJECTION_COPY } from "./claudeCode/copy.js";

export function visibleSubsections(category, controls) {
    return category.subsections.filter((subsection) => !subsection.visible || subsection.visible(controls));
}

function hasConfiguredValue(subsection, document) {
    return subsection.pointers.some((spec) => {
        const value = valueAt(document, typeof spec === "string" ? spec : spec.pointer);
        if (typeof spec === "string" || value === true) return value !== undefined;
        if (!Array.isArray(value)) return false;
        return value.some((member) => spec.members ? spec.members.includes(member) : !spec.exceptMembers.includes(member));
    });
}

function ChangeCount({ count }) {
    if (!count) return null;
    return <span className="text-[11px] text-[#aa742f]"><span className="sr-only">, </span>{count}<span className="sr-only"> changed</span></span>;
}

function scrollToSubsection(id) {
    globalThis.document?.getElementById(`policy-subsection-${id}`)?.scrollIntoView?.({ block: "start", behavior: "smooth" });
}

function CategorySidebar({ definition, active, onChange, tally, controls, onNavigate }) {
    return (
        <nav className="hidden self-start overflow-hidden rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)] xl:block" aria-label="Policy categories">
            <div role="tablist" aria-label="Policy control categories" aria-orientation="vertical" className="grid">
                {definition.categories.map((category, index) => {
                    const selected = active === category.id;
                    return (
                        <div key={category.id} role="presentation" className={index ? "border-t border-[color:var(--line)]" : ""}>
                            <button
                                id={`policy-category-${category.id}`}
                                type="button"
                                role="tab"
                                aria-selected={selected}
                                aria-controls="policy-category-panel"
                                onClick={() => onChange(category.id)}
                                className={[
                                    "flex w-full items-center justify-between gap-[8px] border-0 border-l-2 px-[12px] py-[10px] text-left text-[12px] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[color:var(--green)]",
                                    selected ? "border-[color:var(--green)] bg-[color-mix(in_srgb,var(--ink)_10%,transparent)] text-[color:var(--ink)]" : "border-transparent bg-transparent text-[color:var(--ink)] hover:bg-[color:var(--control-bg)]",
                                ].join(" ")}
                            >
                                {category.label}
                                <ChangeCount count={tally.categories.get(category.id)} />
                            </button>
                            {selected ? (
                                <ul className="m-0 grid list-none border-l-2 border-[color:var(--green)] bg-[color-mix(in_srgb,var(--ink)_10%,transparent)] p-0 pb-[6px]" aria-label={`${category.label} sections`}>
                                    {visibleSubsections(category, controls).map((subsection) => (
                                        <li key={subsection.id}>
                                            <button type="button" className="flex w-full items-center justify-between gap-[8px] border-0 bg-transparent py-[5px] pr-[12px] pl-[26px] text-left text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[color:var(--green)]" onClick={() => onNavigate(subsection.id)}>
                                                {subsection.label}
                                                <ChangeCount count={tally.subsections.get(subsection.id)?.length} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </nav>
    );
}

/// The right column: the draft tally, whatever stops or qualifies a pilot, the
/// composed document, and "Start pilot". Nothing else.
export function DraftTally({ definition, tally, currentGeneration, incomplete, preview, removedPointers, candidate, showDocument, onToggleDocument, canManage, lockedReason, startDisabled, onStartPilot }) {
    const heading = !tally.total
        ? "No unsaved changes"
        : currentGeneration && currentGeneration !== "0"
            ? `${plural(tally.total, "change")} from v${currentGeneration}`
            : plural(tally.total, "change");
    return (
        <aside className="grid content-start gap-[12px] self-start rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[14px] xl:sticky xl:top-[12px]" aria-label="Draft">
            <div className="grid gap-[3px]">
                <span className={EYEBROW_CLASS}>Draft</span>
                <h2 className="m-0 text-[15px] leading-[1.1] font-medium tracking-[-0.03em] text-[color:var(--ink)]">{heading}</h2>
            </div>
            <ChangeList definition={definition} tally={tally} />
            {incomplete.length ? <Blocker>Complete {incomplete.join(" and ")} before starting a pilot.</Blocker> : null}
            {preview?.rejections?.length ? <Blocker>Fix the rejected settings before starting a pilot.</Blocker> : null}
            {preview?.rejections?.map((item, index) => <p key={`${item.pointer}-${index}`} className={`${NOTE_CLASS} m-0`}><code>{item.pointer || "Document"}</code>: {REJECTION_COPY[item.reason] ?? "The server refused this value."}</p>)}
            {removedPointers.length ? <Interaction scope="Current catalog">The next version will remove settings the server no longer accepts: {removedPointers.join(", ")}.</Interaction> : null}
            <button type="button" className="flex justify-between border-0 border-t border-[color:var(--line)] bg-transparent pt-[10px] text-left text-[10px] text-[color:var(--green)]" aria-expanded={showDocument} onClick={onToggleDocument}>{showDocument ? "Hide policy document" : "View policy document"}</button>
            {showDocument && definition.documentNote ? <p className={`${NOTE_CLASS} m-0`}>{definition.documentNote}</p> : null}
            {showDocument ? <pre className="m-0 max-h-[320px] overflow-auto bg-[#171512] p-[10px] text-[9px] leading-[1.5] text-[#eee7dd]">{JSON.stringify(candidate, null, 2)}</pre> : null}
            {!canManage
                ? <p className={`${NOTE_CLASS} m-0 border-t border-[color:var(--line)] pt-[10px]`}>You have read-only access. A tenant administrator can change this policy.</p>
                : lockedReason
                    ? <p className={`${NOTE_CLASS} m-0 border-t border-[color:var(--line)] pt-[10px]`}>{lockedReason}</p>
                    : <button type="button" className={`${PRIMARY_ACTION} min-h-[38px]`} disabled={startDisabled} onClick={onStartPilot}>Start pilot</button>}
        </aside>
    );
}

/// The 6A three-column editor. Below the `xl` breakpoint the sidebar becomes a
/// select above the center column and the tally moves under it.
export default function PolicyEditor({ definition, category, onCategoryChange, tally, editorProps, tallyProps }) {
    const active = definition.categories.find((item) => item.id === category) ?? definition.categories[0];
    // One open subsection at a time: opening one closes the rest.
    const [openId, setOpenId] = useState("");
    const toggle = (id) => setOpenId((current) => (current === id ? "" : id));
    const navigate = (id) => { setOpenId(id); scrollToSubsection(id); };
    return (
        <div className="grid items-start gap-[16px] xl:grid-cols-[230px_minmax(0,1fr)_290px]">
            <CategorySidebar definition={definition} active={active.id} onChange={onCategoryChange} tally={tally} controls={editorProps.controls} onNavigate={navigate} />
            <div className="grid min-w-0 gap-[12px]">
                <p className={`${NOTE_CLASS} m-0`}>Unconfigured settings keep their existing behavior.</p>
                <label className="grid gap-[5px] xl:hidden">
                    <span className="text-[11px] font-medium text-[color:var(--ink)]">Policy category</span>
                    <Select ariaLabel="Policy category" value={active.id} onChange={onCategoryChange} options={definition.categories.map((item) => ({ value: item.id, label: item.label }))} />
                </label>
                <div id="policy-category-panel" role="tabpanel" aria-labelledby={`policy-category-${active.id}`} className="grid content-start gap-[12px]">
                    <h2 className="m-0 text-[15px] leading-[1.1] font-medium tracking-[-0.03em] text-[color:var(--ink)]">{active.label}</h2>
                    {visibleSubsections(active, editorProps.controls).map((subsection) => {
                        const Component = subsection.Component;
                        const open = openId === subsection.id;
                        return (
                            <section key={subsection.id} id={`policy-subsection-${subsection.id}`} aria-label={subsection.label} className={`policy-subsection scroll-mt-[12px] rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[12px] ${open ? "" : "is-collapsed"}`}>
                                <div className={`grid grid-cols-[auto_minmax(0,1fr)] gap-[8px] ${open ? "" : "cursor-pointer"}`} onClick={(event) => {
                                    // The title (the body's first child) collapses an open subsection; clicks
                                    // on its controls must not.
                                    if (!open || event.target.closest(".policy-subsection-body > :first-child")) toggle(subsection.id);
                                }}>
                                    <button type="button" className="relative z-[1] mt-[1px] h-[16px] w-[16px] shrink-0 border-0 bg-transparent p-0 text-[12px] text-[color:var(--muted)] hover:text-[color:var(--ink)]" aria-expanded={open} aria-label={`${open ? "Collapse" : "Expand"} ${subsection.label}`} onClick={(event) => { event.stopPropagation(); toggle(subsection.id); }}>{open ? "▾" : "▸"}</button>
                                    <div className="policy-subsection-body grid gap-[12px]">
                                        <Component {...editorProps} />
                                        {subsection.pilotCheck && hasConfiguredValue(subsection, tallyProps.candidate) ? <WorkflowImpact test={subsection.pilotCheck} /> : null}
                                    </div>
                                </div>
                            </section>
                        );
                    })}
                </div>
            </div>
            <DraftTally definition={definition} tally={tally} {...tallyProps} />
        </div>
    );
}
